import { DropdownMenu as RadMenu } from 'radix-ui'
import useMediaSelectionActions from '@/actions/useMediaSelectionActions'
import BellIcon from '@/CustomIcons/BellIcon'
import CurvedArrowRightIcon from '@/CustomIcons/CurvedArrowRightIcon'
import GearIcon from '@/CustomIcons/GearIcon'
import OptionsEllipsesIcon from '@/CustomIcons/OptionsEllipsesIcon'
import PinIcon from '@/CustomIcons/PinIcon'
import { useRoomContext } from '@/RoomContext'
import { FeatureFlags } from '@/services/FeatureFlags'
import useAnalytics from '@/services/useAnalytics'
import useMiscRoomActions from '@/services/useMiscRoomActions'
import useRoomActions from '@/services/useRoomActions'
import { Button } from '@/UIComponents2/Button'
import { SidebarTooltip as Tooltip } from './SidebarTooltip'
import { useUserContext } from '@/UserContext'
import { cn } from '@/utlis/cn'
import { useTranslation } from 'react-i18next'
import MicrophoneIcon from '@/CustomIcons/MicrophoneIcon'
import MicrophoneOffIcon from '@/CustomIcons/MicrophoneOffIcon'
import CallIcon from '@/CustomIcons/CallIcon'
import { isThinStripSidebar, sessionViewModeState, useViewMode, ViewMode } from '../shared/useViewMode'
import { env } from '@/services/EnvironmentVariables'
import { sessionSettingsTrigger } from '../../SessionPage'
import { SimpleMenuItem } from '@/UIComponents2/SimpleMenu'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import { NotificationsPopover } from '@/components/NotificationsPopover'
import CommunicationIcon from '@/CustomIcons/CommunicationIcon'
import { chatTrigger } from './Sidebar'
import { Badge } from '@/UIComponents2/Badge'
import useChatRoomController from '@/pages/ChatRoom/useChatRoomController'
import { LayoutMenu } from './LayoutMenu'
import GalleryHideIcon from '@/CustomIcons/GalleryHideIcon'
import GalleryShowIcon from '@/CustomIcons/GalleryShowIcon'
import CollapseIcon from '@/CustomIcons/CollapseIcon'
import { useContext } from 'react'
import { PortalContext } from '@/UIComponents2/PortalContext'
import '@/components/dropdown-animation.css'

export default function SidebarMenu({ chatController }: { chatController: ReturnType<typeof useChatRoomController> }) {
    const { t } = useTranslation()
    const [userState] = useUserContext()

    const { leaveRoomStart, calcIsMyAudioDeviceAllMuted, toggleMyAudioDeviceMute } = useRoomActions()

    const { windowRetract, windowRevert, windowThinStripRight } = useMiscRoomActions()

    const viewMode = useViewMode()
    const selfMuted = calcIsMyAudioDeviceAllMuted()

    const handleToggleSidebarCollapse = () => {
        const collapsed = !sessionViewModeState.mode.sidebarCollapsed
        sessionViewModeState.setSidebarCollapsed(collapsed)

        if (env.IS_ELECTRON) {
            if (sessionViewModeState.mode.galleryHidden) {
                if (collapsed) {
                    windowThinStripRight()
                } else {
                    windowRetract({
                        doNotRestoreSavedWindowDimension: false,
                    })
                }
            }
        }
    }

    const handleToggleGalleryHidden = () => {
        const hidden = !sessionViewModeState.mode.galleryHidden
        sessionViewModeState.setGalleryHidden(hidden)
        if (env.IS_ELECTRON) {
            if (hidden) {
                if (sessionViewModeState.mode.sidebarCollapsed) {
                    windowThinStripRight()
                } else {
                    windowRetract({
                        doNotRestoreSavedWindowDimension: false,
                    })
                }
            } else {
                windowRevert({ doNotRestoreSavedWindowDimension: false })
            }
        }
    }

    const renderNotifications =
        !userState.isGuest &&
        userState.clientType !== 'listenonly' &&
        !viewMode.sidebarCollapsed &&
        !viewMode.sidebarPoppedOut &&
        !viewMode.galleryHidden

    return (
        <div
            className={cn(
                'flex row-gap-[5px]',
                viewMode.sidebarCollapsed ? 'flex-col-reverse items-center' : 'flex-row pb-2',
                viewMode.sidebarPoppedOut && 'app-window-drag-region'
            )}
            style={{
                flexDirection: viewMode.sidebarCollapsed ? 'column-reverse' : 'row',
                justifyContent: viewMode.sidebarCollapsed ? 'flex-end' : 'space-between',
                height: viewMode.sidebarCollapsed ? '100%' : undefined,
                rowGap: '5px',
            }}
        >
            {viewMode.sidebarCollapsed && (
                <div className={cn('flex flex-col flex-grow justify-end', isThinStripSidebar(viewMode) && 'pb-1')}>
                    <Tooltip className="w-fit h-fit" position="left" text={t('Chat Room')}>
                        <Button
                            variant="tertiary"
                            onClick={() => {
                                handleToggleSidebarCollapse()
                                setTimeout(() => {
                                    chatTrigger.trigger(true)
                                }, 20)
                            }}
                        >
                            <div className="relative">
                                <CommunicationIcon style={{ fontSize: '1.3rem' }} />
                                {chatController.unreadMessagesCt > 0 && (
                                    <Badge className="top-[-6px] right-[-6px]">
                                        <span>{chatController.unreadMessagesCt}</span>
                                    </Badge>
                                )}
                            </div>
                        </Button>
                    </Tooltip>
                </div>
            )}
            <div
                className={cn('flex gap-[5px] justify-end items-center')}
                style={{
                    flexDirection: viewMode.sidebarCollapsed ? 'column-reverse' : 'row',
                }}
            >
                {!viewMode.sidebarPoppedOut && FeatureFlags.isEnabled('desktop-app-only-feature') && (
                    <Tooltip
                        position={viewMode.sidebarCollapsed ? 'left' : 'bottom'}
                        text={viewMode.galleryHidden ? t('Expand main view') : t('Collapse main view')}
                    >
                        <Button variant="tertiary" onClick={handleToggleGalleryHidden}>
                            {viewMode.galleryHidden ? (
                                <GalleryShowIcon className="text-muse-fg/85" style={{ fontSize: '1.3rem' }} />
                            ) : (
                                <GalleryHideIcon className="text-muse-fg/85" style={{ fontSize: '1.3rem' }} />
                            )}
                        </Button>
                    </Tooltip>
                )}

                {!viewMode.sidebarPoppedOut &&
                    FeatureFlags.isEnabled('desktop-app-only-feature') &&
                    viewMode.galleryHidden && (
                        <Tooltip
                            position={viewMode.sidebarCollapsed ? 'left' : 'bottom'}
                            text={viewMode.alwaysOnTop ? t('Unset pin to top') : t('Pin Muse window to top')}
                        >
                            <Button
                                variant="tertiary"
                                onClick={() =>
                                    sessionViewModeState.setAlwaysOnTop(!sessionViewModeState.mode.alwaysOnTop)
                                }
                            >
                                <PinIcon outlineOnly={!viewMode.alwaysOnTop} style={{ fontSize: '1.3rem' }} />
                            </Button>
                        </Tooltip>
                    )}

                {viewMode.sidebarPoppedOut && (
                    <Tooltip
                        text={t('Exit fullscreen')}
                        position="left" // Unsure if this is right
                    >
                        <Button variant="tertiary" onClick={() => sessionViewModeState.toggleFullscreen()}>
                            <CollapseIcon />
                        </Button>
                    </Tooltip>
                )}

                {!viewMode.sidebarCollapsed && <LayoutMenu />}
            </div>
            <div
                className={cn('flex gap-[5px] justify-end')}
                style={{
                    flexDirection: viewMode.sidebarCollapsed ? 'column-reverse' : 'row',
                }}
            >
                {renderNotifications && (
                    <NotificationsPopover>
                        <Button variant="tertiary">
                            <BellIcon style={{ fontSize: '1.3rem' }} />
                        </Button>
                    </NotificationsPopover>
                )}
                {userState.clientType !== 'listenonly' && (
                    <Button variant="tertiary" onClick={() => sessionSettingsTrigger.trigger({ tab: 'audio' })}>
                        <GearIcon style={{ fontSize: '1.3rem' }} />
                    </Button>
                )}
                {viewMode.sidebarCollapsed && (
                    <Tooltip position={'left'} text={selfMuted ? t('Unmute all') : t('Mute all')}>
                        <Button variant="tertiary" onClick={() => toggleMyAudioDeviceMute()}>
                            {selfMuted ? (
                                <MicrophoneOffIcon className="text-muse-danger" style={{ fontSize: '1.3rem' }} />
                            ) : (
                                <MicrophoneIcon style={{ fontSize: '1.3rem' }} />
                            )}
                        </Button>
                    </Tooltip>
                )}
                {isThinStripSidebar(viewMode) ? (
                    <Tooltip position="left" text={t('Leave session')}>
                        <Button variant="tertiary" onClick={() => leaveRoomStart({ endSession: false })}>
                            <CallIcon className="text-[rgba(255,2,2,1)]" style={{ fontSize: '1.3rem' }} />
                        </Button>
                    </Tooltip>
                ) : (
                    <Menu
                        viewMode={viewMode}
                        onToggleGalleryHidden={handleToggleGalleryHidden}
                        onToggleSidebarCollapse={handleToggleSidebarCollapse}
                    />
                )}
                {viewMode.sidebarCollapsed && (
                    <Tooltip
                        position={viewMode.sidebarCollapsed ? 'left' : 'bottom'}
                        text={viewMode.sidebarCollapsed ? t('Expand Sidebar') : t('Collapse Sidebar')}
                    >
                        <Button variant="tertiary" onClick={handleToggleSidebarCollapse}>
                            <CurvedArrowRightIcon
                                className={cn(viewMode.sidebarCollapsed ? 'rotate-180' : '')}
                                style={{ fontSize: '1.3rem' }}
                            />
                        </Button>
                    </Tooltip>
                )}
            </div>
        </div>
    )
}

type MenuProps = {
    viewMode: ViewMode
    onToggleGalleryHidden: () => void
    onToggleSidebarCollapse: () => void
}

function Menu(props: MenuProps) {
    const { t } = useTranslation()
    const [userState] = useUserContext()
    const [roomState] = useRoomContext()

    const { logUserActivityWithContext } = useAnalytics()
    const { openShareScreenDialog } = useMediaSelectionActions()

    const { toggleMuteAllPeers, leaveRoomStart, testConnectionFailure, isRemoteAudioDeviceAllMutedForAllPeers } =
        useRoomActions()

    const { unhideAllVidoesFromGallery, setShowWaitingRoomPopup } = useMiscRoomActions()

    const portalContainer = useContext(PortalContext)

    const menuItemGroups = (() => {
        type MenuGroup = {
            label?: string
            items: SimpleMenuItem[]
        }
        const viewGroup: MenuGroup = { label: t('View'), items: [] }
        const settingsGroup: MenuGroup = { label: t('Settings'), items: [] }
        const actionsGroup: MenuGroup = { label: t('Session Actions'), items: [] }
        const otherGroup: MenuGroup = { items: [] }

        if (!roomState.isSidebarDetached && FeatureFlags.isEnabled('desktop-app-only-feature')) {
            viewGroup.items.push({
                label: props.viewMode.galleryHidden ? t('Expand View') : t('Collapse View'),
                onClick: props.onToggleGalleryHidden,
            })
        }

        viewGroup.items.push({
            label: props.viewMode.sidebarCollapsed ? t('Expand Sidebar') : t('Collapse Sidebar'),
            onClick: props.onToggleSidebarCollapse,
        })

        if (FeatureFlags.isEnabled('desktop-app-only-feature')) {
            viewGroup.items.push({
                label: `${props.viewMode.alwaysOnTop ? '\u2713' : ''} ${t('Always on Top')}`,
                onClick: () => sessionViewModeState.setAlwaysOnTop(!props.viewMode.alwaysOnTop),
            })

            viewGroup.items.push({
                label: props.viewMode.fullscreen ? t('Exit Fullscreen') : t('Enter Fullscreen'),
                onClick: () => sessionViewModeState.toggleFullscreen(),
            })
        }

        viewGroup.items.push({
            label: t('Unhide all videos from gallery'),
            onClick: unhideAllVidoesFromGallery,
        })

        if (userState.clientType !== 'listenonly') {
            settingsGroup.items.push(
                {
                    label: t('Session Settings'),
                    onClick: () => {
                        sessionSettingsTrigger.trigger({ tab: 'session' })
                        logUserActivityWithContext(AnalyticsEvents.CLICKED_SESSION_SETTINGS_BUTTON)
                    },
                },
                {
                    label: t('Overview Settings'),
                    onClick: () => {
                        sessionSettingsTrigger.trigger({ tab: 'audio' })
                        logUserActivityWithContext(AnalyticsEvents.CLICKED_OVERVIEW_SETTINGS_BUTTON)
                    },
                }
            )
            actionsGroup.items.push(
                {
                    label: t('Invite Collaborators'),
                    onClick: () => {
                        setShowWaitingRoomPopup(true)
                    },
                },
                {
                    label: t('Share Screen'),
                    onClick: () => {
                        openShareScreenDialog()
                        logUserActivityWithContext(AnalyticsEvents.CLICKED_SHARE_SCREEN_BUTTON, {
                            componentSource: 'SideBar',
                        })
                    },
                }
            )

            if (FeatureFlags.isEnabled('desktop-app-only-feature')) {
                actionsGroup.items.push({
                    label: t('Record Screen'),
                    onClick: () => {
                        sessionSettingsTrigger.trigger({ tab: 'recording' })
                    },
                })
            }

            actionsGroup.items.push({
                label: isRemoteAudioDeviceAllMutedForAllPeers ? t('Unmute All') : t('Mute All'),
                onClick: toggleMuteAllPeers,
            })

            if (env.TEST_RTC_CONNECTION_ERROR) {
                otherGroup.items.push(
                    {
                        label: 'TEST: Connection failure',
                        onClick: () => testConnectionFailure(['connection']),
                    },
                    {
                        label: 'TEST: SignalingWS failure',
                        onClick: () => testConnectionFailure(['signaling_ws']),
                    }
                )
            }
        }

        otherGroup.items.push({
            label: t('Leave Session'),
            className: 'text-muse-danger',
            onClick: () => {
                leaveRoomStart({ endSession: false })
            },
        })

        return [viewGroup, settingsGroup, actionsGroup, otherGroup]
    })()

    return (
        <RadMenu.Root modal={false}>
            <RadMenu.Trigger asChild>
                <Button variant="tertiary">
                    <OptionsEllipsesIcon style={{ fontSize: '1.3rem' }} />
                </Button>
            </RadMenu.Trigger>

            <RadMenu.Portal container={portalContainer}>
                <RadMenu.Content
                    collisionPadding={15}
                    sideOffset={5}
                    className="dropdown-menu-content z-30 py-2 bg-muse-menu rounded-[16px] shadow-glow min-w-[200px]"
                >
                    {menuItemGroups.map((group, i) => (
                        <RadMenu.Group key={i}>
                            {group.label && (
                                <RadMenu.Label className="w-full py-1 px-4 text-muse-fg/60">
                                    {group.label}
                                </RadMenu.Label>
                            )}
                            {group.items.map((item, j) => (
                                <RadMenu.Item
                                    key={j}
                                    className={cn(
                                        'cursor-pointer w-full px-6 py-2 hover:bg-white/10 transition-colors duration-250',
                                        item?.className
                                    )}
                                    onClick={(ev) => {
                                        if (!(item.closeOnSelect ?? true)) {
                                            ev.preventDefault()
                                        }
                                        item.onClick?.()
                                    }}
                                >
                                    {item.label}
                                </RadMenu.Item>
                            ))}
                            {i < menuItemGroups.length - 1 && (
                                <RadMenu.Separator className="h-[1px] bg-white/10 mb-1" />
                            )}
                        </RadMenu.Group>
                    ))}
                </RadMenu.Content>
            </RadMenu.Portal>
        </RadMenu.Root>
    )
}
